import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import { ApiParams } from '../../common/api/api';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import StatusLabel from '../../common/StatusLabel';
import { User } from '../../user/User.interface';
import { UserContext } from '../../user/User.context';
import { useFindTrainees } from '../../user/User.hooks';
import { Group } from '../groups/Group.interface';
import TraineeListFilter, {
  TraineeFilterOptions
} from './components/TraineeListFilter';

const TrainingAdminTrainees: React.FC = () => {
  const { user } = useContext(UserContext);
  const hasAccess = user?.roles?.some(
    r => r.name === 'Admin' || r.name === 'SiteAdmin'
  );
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam(
    'order',
    withDefault(StringParam, 'user.id')
  );
  const [direction, setDirection] = useQueryParam(
    'direction',
    withDefault(StringParam, 'ASC')
  );
  const [status, setStatus] = useQueryParam('status', StringParam);
  const [apiParams, setApiParams] = useState<ApiParams>({ status });

  const history = useHistory();
  const { users, get } = useFindTrainees();

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  useEffect(() => {
    get({ search, page, limit, order, direction, ...apiParams });
  }, [page, limit, order, direction, search, apiParams, get]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Trainees</h2>
        <SearchBox value={search} onSearch={value => setSearch(value)}>
          <TraineeListFilter
            options={{ status: status || undefined }}
            onFilter={(options: TraineeFilterOptions) => {
              const { status, groupId } = options;
              const params: ApiParams = { status, groupId };
              !status && delete params.status;
              !groupId && delete params.groupId;
              setStatus(status);
              setApiParams(params);
            }}
          />
        </SearchBox>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.id'}
                    direction={getSortDirection('user.id')}
                    onClick={() => handleSort('user.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.firstName'}
                    direction={getSortDirection('user.firstName')}
                    onClick={() => handleSort('user.firstName')}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.lastName'}
                    direction={getSortDirection('user.lastName')}
                    onClick={() => handleSort('user.lastName')}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.email'}
                    direction={getSortDirection('user.email')}
                    onClick={() => handleSort('user.email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>Teams</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'compliance'}
                    direction={getSortDirection('compliance')}
                    onClick={() => handleSort('compliance')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {users?.data.map((user: User, index: number) => (
                <TableRow
                  key={index}
                  hover={hasAccess}
                  onClick={() => {
                    hasAccess &&
                      history.push(`/portal/admin/trainee/${user.id}/detail`);
                  }}
                >
                  <TableCell>USER-{user.id}</TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell style={{ whiteSpace: 'normal' }}>
                    {user.groups?.map((group: Group) => group.name).join(', ')}
                  </TableCell>
                  <TableCell>
                    {user.compliance && (
                      <StatusLabel
                        color={
                          user.compliance === 'compliant'
                            ? 'completed'
                            : user.compliance === 'in-progress'
                            ? 'in-progress'
                            : 'incomplete'
                        }
                        status={user.compliance}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={users?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, page: number) => setPage(page)}
          onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>
    </PageTemplate>
  );
};

export default TrainingAdminTrainees;
